import { Season } from '@shared/constants';
import { IEnhancedTeamStats, IGameStats, ITeamDetails, SeasonID, SeasonWeekID } from '@shared/types';
import { matchupBoxScoreConverter } from 'api/converters/MatchBoxScoreConverter';
import { teamDetailsConverter, teamDetailsToDoc } from 'api/converters/TeamDetailsConverter';
import { teamMatchResultsConverter } from 'api/converters/TeamMatchResultsConverter';
import { ActionCls, Cls, Emojis, END, LocationCls, ValueCls, ValueLgCls } from 'app/constants';
import { collection, doc, getDoc, getDocs, updateDoc, writeBatch } from 'firebase/firestore';
import { breakByeWeekID, toSeasonWeekID, toWeekID } from 'helpers/IdHelpers';
import { to4CharTeamID } from 'helpers/TeamHelpers';
import { ByeWeekID, ITeamsOnByeWeek, TeamsOnByeWeekBySeasonWeek } from 'types';
import { db } from '../lib/firebase/Firebase';

export const saveTeamDetails = async (teamDetails: ITeamDetails): Promise<void> => {
  const msg = `${Emojis.eyes} %c START SINGLE TEAM UPDATE %c`;
  console.log(msg, Cls.Act, '');

  const fbTeamID = to4CharTeamID(teamDetails.teamID);
  const docRef = doc(db, `teams/${fbTeamID}`).withConverter(teamDetailsConverter);

  await updateDoc(docRef, { ...teamDetails });

  console.log(`${Emojis.eyes} %c END UPDATE %c for team %c ${teamDetails.teamAbrv} %c`, Cls.Act, Cls.Info);
  return;
};

export const saveDetailsForAllTeams = async (teams: ITeamDetails[]): Promise<void> => {
  const msg = `${Emojis.eyes} %c START BATCH UPDATE ${END} %c teams ${END} %c count: ${teams.length} ${END}`;
  console.log(msg, ActionCls, ValueCls, ValueLgCls);

  const batch = writeBatch(db);

  teams.forEach((team: ITeamDetails) => {
    const fbTeamID = to4CharTeamID(team.teamID);
    const collectionData = collection(db, 'teams').withConverter(teamDetailsConverter);
    const teamRef = doc(collectionData, fbTeamID);
    batch.set(teamRef, teamDetailsToDoc(team), { merge: true });
  });

  await batch.commit();
  console.log(`${Emojis.eyes} %c END BATCH UPDATE ${END} %c teams ${END}`, ActionCls, ValueCls);
  return;
};

export const fetchTeamDetails = async (): Promise<ITeamDetails[]> => {
  const collectionData = collection(db, 'teams').withConverter(teamDetailsConverter);
  const snapshot = await getDocs(collectionData);
  const teams: ITeamDetails[] = [];
  snapshot.forEach((doc) => {
    const team = doc.data();
    teams.push(team);
  });
  return teams;
};

// #region TEAM MATCH RESULTS ACTIONS *****************************************
export const saveTeamResults = async (teamResults: IEnhancedTeamStats[], collectionPath: string): Promise<void> => {
  const batch = writeBatch(db);

  teamResults.forEach((result: IEnhancedTeamStats) => {
    const matchRef = doc(db, collectionPath, result.uID).withConverter(teamMatchResultsConverter);
    batch.set(matchRef, { ...result });
  });

  console.log(`${Emojis.eyes} %c BATCH WRITING ${END} %c ${collectionPath} ${END}`, ActionCls, ValueCls);

  return await batch.commit();
};

export const saveTeamMatchResults = async (teamMatchResults: IEnhancedTeamStats[], year: SeasonID): Promise<void> => {
  return await saveTeamResults(teamMatchResults, `stats/${year}/teammatchresults`);
};

export const saveOldTeamMatchResults = async (teamResults: IEnhancedTeamStats[], year: SeasonID): Promise<void> => {
  return await saveTeamResults(teamResults, `teammatchresults${year}`);
};

export const fetchTeamResults = async (collectionPath: string): Promise<IEnhancedTeamStats[]> => {
  const collectionData = collection(db, collectionPath).withConverter(teamMatchResultsConverter);
  const snapshot = await getDocs(collectionData);
  const matches: IEnhancedTeamStats[] = [];
  snapshot.forEach((doc) => {
    const match = doc.data();
    matches.push(match);
  });
  return matches;
};

export const fetchMatchResults = async (collectionPath: string): Promise<IGameStats[]> => {
  const collectionData = collection(db, collectionPath).withConverter(matchupBoxScoreConverter);
  const snapshot = await getDocs(collectionData);
  const matches: IGameStats[] = [];
  snapshot.forEach((doc) => {
    const match = doc.data();
    matches.push(match);
  });
  return matches;
};

export const fetchTeamMatchResults = async (year: SeasonID = Season.Season2024): Promise<IEnhancedTeamStats[]> => {
  return await fetchTeamResults(`stats/${year}/teammatchresults`);
};

export const fetchGameMatchResults = async (year: SeasonID = Season.Season2024): Promise<IGameStats[]> => {
  return await fetchMatchResults(`stats/${year}/matchresults`);
};

export const fetchOldTeamMatchResults = async (year: SeasonID = Season.Season2024): Promise<IEnhancedTeamStats[]> => {
  return await fetchTeamResults(`teammatchresults${year}`);
};
// #endregion

// #region TEAM BYE WEEKS *****************************************************

/**
 * Fetches the bye weeks for a specific season.
 *
 * @param {SeasonID} year SeasonID, e.g. '2024'
 *
 * @return {Promise<ByeWeekID[]>} An array of ByeWeekID objects
 */
export const fetchTeamsOnByeWeekBySeasonID = async (year: SeasonID): Promise<ByeWeekID[]> => {
  const seasonRef = doc(db, `stats/${year}`);
  const docSnap = await getDoc(seasonRef);
  if (docSnap.exists()) {
    const seasonData = docSnap.data();
    // Season documents may exist before bye-week membership has been populated.
    const byeWeeks = (seasonData.byeWeeks ?? []) as ByeWeekID[];
    return byeWeeks;
  }
  console.warn(`${Emojis.eyes} No bye week data found for season: %c ${year} %c`, ValueCls, '');
  return [];
};

export const convertTeamOnByeWeeksMapToArray = (teamsOnByeMap: TeamsOnByeWeekBySeasonWeek): ITeamsOnByeWeek[] => {
  return Object.keys(teamsOnByeMap).map((key) => teamsOnByeMap[key as SeasonWeekID]);
};

export const createTeamsOnByeWeekCollection = (
  byeWeeks: ByeWeekID[],
  seasonID: SeasonID,
): TeamsOnByeWeekBySeasonWeek => {
  const bw: TeamsOnByeWeekBySeasonWeek = {};

  for (let i = 1; i <= 18; i++) {
    const weekStr = i.toString().padStart(2, '0');
    const swID = toSeasonWeekID(seasonID, weekStr);
    bw[swID] = { teamIDs: [], weekID: toWeekID(i), year: seasonID, seasonWeekID: swID };
  }

  byeWeeks.forEach((byeWeekID) => {
    const { weekID, teamID } = breakByeWeekID(byeWeekID);
    const swID = toSeasonWeekID(seasonID, weekID);
    if (bw[swID]) {
      bw[swID].teamIDs.push(teamID);
    } else {
      const msg = `${Emojis.eyes} %c createTeamsOnByeWeekCollection %c No entry for SeasonWeekID: %c ${swID} %c`;
      console.warn(msg, LocationCls, '', ValueCls, '');
    }
  });

  return bw;
};

/**
 * Fetch bye-week membership for one requested season, or all stored seasons for
 * legacy full-store callers. Empty membership still produces a row for each week.
 * @param {SeasonID} [year] Requested season; omit to discover seasons in /stats.
 * @return {Promise<ITeamsOnByeWeek[]>} Season/week rows, including weeks with no byes.
 */
export const fetchTeamsOnByeWeek = async (year?: SeasonID): Promise<ITeamsOnByeWeek[]> => {
  if (year) {
    const byeWeeks = await fetchTeamsOnByeWeekBySeasonID(year);
    return convertTeamOnByeWeeksMapToArray(createTeamsOnByeWeekCollection(byeWeeks, year));
  }
  const seasons = await getDocs(collection(db, 'stats'));
  const records = await Promise.all(seasons.docs
    .filter((season) => /^\d{4}$/.test(season.id))
    .map((season) => fetchTeamsOnByeWeek(season.id as SeasonID)));
  return records.flat();
};

// #endregion
